import { Injectable } from '@angular/core';
import { take, tap } from 'rxjs/operators';
import { ModelTreeLevelsEnum } from '#common/enums/model-tree-levels-enum.enum';
import { ResponseInfoStatusEnum } from '#common/enums/response-info-status.enum';
import { ToBackendRequestInfoNameEnum } from '#common/enums/to/to-backend-request-info-name.enum';
import { isDefined } from '#common/functions/is-defined';
import { isUndefined } from '#common/functions/is-undefined';
import type { ProjectChartLink } from '#common/zod/backend/project-chart-link';
import type { ProjectDashboardLink } from '#common/zod/backend/project-dashboard-link';
import type { ProjectExplorerSessionLink } from '#common/zod/backend/project-explorer-session-link';
import type { ProjectModelLink } from '#common/zod/backend/project-model-link';
import type { ProjectReportLink } from '#common/zod/backend/project-report-link';
import type { ProjectSelectedGivenLink } from '#common/zod/backend/project-selected-given-link';
import type { Ui } from '#common/zod/backend/ui';
import type {
  ToBackendSetUserUiRequestPayload,
  ToBackendSetUserUiResponse
} from '#common/zod/to-backend/users/to-backend-set-user-ui';
import { NavQuery } from '../queries/nav.query';
import { UiQuery } from '../queries/ui.query';
import { ApiService } from './api.service';

@Injectable({ providedIn: 'root' })
export class UiService {
  constructor(
    private apiService: ApiService,
    private uiQuery: UiQuery,
    private navQuery: NavQuery
  ) {}

  async setUserUi(item: {
    timezone?: string;
    modelTreeLevels?: ModelTreeLevelsEnum;
    projectModelLinks?: ProjectModelLink[];
    projectChartLinks?: ProjectChartLink[];
    projectDashboardLinks?: ProjectDashboardLink[];
    projectExplorerSessionLinks?: ProjectExplorerSessionLink[];
    projectReportLinks?: ProjectReportLink[];
    projectSelectedGivenLinks?: ProjectSelectedGivenLink[];
    chartsByModel?: boolean;
    permissionsAutoAcceptSessionIds?: string[];
    newSessionPermissionsAutoAccept?: boolean;
    newSessionExplorerModelExtraId?: string;
    newSessionExplorerVariant?: string;
    newSessionEditorModelExtraId?: string;
    newSessionEditorVariant?: string;
  }) {
    let {
      timezone,
      modelTreeLevels,
      projectModelLinks,
      projectChartLinks,
      projectDashboardLinks,
      projectExplorerSessionLinks,
      projectReportLinks,
      projectSelectedGivenLinks,
      chartsByModel,
      permissionsAutoAcceptSessionIds,
      newSessionPermissionsAutoAccept,
      newSessionExplorerModelExtraId,
      newSessionExplorerVariant,
      newSessionEditorModelExtraId,
      newSessionEditorVariant
    } = item;

    let uiState = this.uiQuery.getValue();

    let ui: Ui = {
      modelTreeLevels: isDefined(modelTreeLevels)
        ? modelTreeLevels
        : uiState.modelTreeLevels,
      timezone: isDefined(timezone) ? timezone : uiState.timezone,
      timeSpec: uiState.timeSpec,
      timeRangeFraction: uiState.timeRangeFraction,
      projectModelLinks: isDefined(projectModelLinks)
        ? projectModelLinks
        : uiState.projectModelLinks,
      projectChartLinks: isDefined(projectChartLinks)
        ? projectChartLinks
        : uiState.projectChartLinks,
      projectDashboardLinks: isDefined(projectDashboardLinks)
        ? projectDashboardLinks
        : uiState.projectDashboardLinks,
      projectExplorerSessionLinks: isDefined(projectExplorerSessionLinks)
        ? projectExplorerSessionLinks
        : uiState.projectExplorerSessionLinks,
      projectReportLinks: isDefined(projectReportLinks)
        ? projectReportLinks
        : uiState.projectReportLinks,
      projectSelectedGivenLinks: isDefined(projectSelectedGivenLinks)
        ? projectSelectedGivenLinks
        : uiState.projectSelectedGivenLinks,
      chartsByModel: isDefined(chartsByModel)
        ? chartsByModel
        : uiState.chartsByModel,
      permissionsAutoAcceptSessionIds: isDefined(
        permissionsAutoAcceptSessionIds
      )
        ? permissionsAutoAcceptSessionIds
        : uiState.permissionsAutoAcceptSessionIds,
      newSessionPermissionsAutoAccept: isDefined(
        newSessionPermissionsAutoAccept
      )
        ? newSessionPermissionsAutoAccept
        : uiState.newSessionPermissionsAutoAccept,
      newSessionExplorerModelExtraId: isDefined(newSessionExplorerModelExtraId)
        ? newSessionExplorerModelExtraId
        : uiState.newSessionExplorerModelExtraId,
      newSessionExplorerVariant: isDefined(newSessionExplorerVariant)
        ? newSessionExplorerVariant
        : uiState.newSessionExplorerVariant,
      newSessionEditorModelExtraId: isDefined(newSessionEditorModelExtraId)
        ? newSessionEditorModelExtraId
        : uiState.newSessionEditorModelExtraId,
      newSessionEditorVariant: isDefined(newSessionEditorVariant)
        ? newSessionEditorVariant
        : uiState.newSessionEditorVariant
    };

    let payload: ToBackendSetUserUiRequestPayload = {
      ui: ui
    };

    this.apiService
      .req({
        pathInfoName: ToBackendRequestInfoNameEnum.ToBackendSetUserUi,
        payload: payload
      })
      .pipe(
        tap((resp: ToBackendSetUserUiResponse) => {
          if (resp.info?.status === ResponseInfoStatusEnum.Ok) {
          }
        }),
        take(1)
      )
      .subscribe();
  }

  setProjectChartLink(item: { chartId: string }) {
    let { chartId } = item;

    let nav = this.navQuery.getValue();

    if (isUndefined(chartId) || isUndefined(nav.projectId)) {
      return;
    }

    let links = this.uiQuery.getValue().projectChartLinks;

    let link: ProjectChartLink = links.find(l => l.projectId === nav.projectId);

    if (link?.chartId === chartId) {
      return;
    }

    let newProjectChartLinks;

    if (isDefined(link)) {
      let newLink: ProjectChartLink = {
        projectId: nav.projectId,
        chartId: chartId,
        navTs: Date.now()
      };

      newProjectChartLinks = [
        newLink,
        ...links.filter(r => !(r.projectId === nav.projectId))
      ];
    } else {
      let newLink: ProjectChartLink = {
        projectId: nav.projectId,
        chartId: chartId,
        navTs: Date.now()
      };

      newProjectChartLinks = [newLink, ...links];
    }

    let oneYearAgoTimestamp = Date.now() - 1000 * 60 * 60 * 24 * 365;

    newProjectChartLinks = newProjectChartLinks.filter(
      l => l.navTs >= oneYearAgoTimestamp
    );

    this.uiQuery.updatePart({ projectChartLinks: newProjectChartLinks });
    this.setUserUi({ projectChartLinks: newProjectChartLinks });
  }

  setProjectModelLink(item: { modelId: string }) {
    let { modelId } = item;

    let nav = this.navQuery.getValue();

    if (isUndefined(modelId) || isUndefined(nav.projectId)) {
      return;
    }

    let links = this.uiQuery.getValue().projectModelLinks;

    let link: ProjectModelLink = links.find(l => l.projectId === nav.projectId);

    if (link?.modelId === modelId) {
      return;
    }

    let newProjectModelLinks;

    if (isDefined(link)) {
      let newLink = {
        projectId: nav.projectId,
        modelId: modelId,
        navTs: Date.now()
      };

      newProjectModelLinks = [
        newLink,
        ...links.filter(r => !(r.projectId === nav.projectId))
      ];
    } else {
      let newLink = {
        projectId: nav.projectId,
        modelId: modelId,
        navTs: Date.now()
      };

      newProjectModelLinks = [newLink, ...links];
    }

    let oneYearAgoTimestamp = Date.now() - 1000 * 60 * 60 * 24 * 365;

    newProjectModelLinks = newProjectModelLinks.filter(
      l => l.navTs >= oneYearAgoTimestamp
    );

    this.uiQuery.updatePart({ projectModelLinks: newProjectModelLinks });
    this.setUserUi({ projectModelLinks: newProjectModelLinks });
  }

  setProjectDashboardLink(item: { dashboardId: string }) {
    let { dashboardId } = item;

    let nav = this.navQuery.getValue();

    if (isUndefined(dashboardId) || isUndefined(nav.projectId)) {
      return;
    }

    let links = this.uiQuery.getValue().projectDashboardLinks;

    let link: ProjectDashboardLink = links.find(
      l => l.projectId === nav.projectId
    );

    if (link?.dashboardId === dashboardId) {
      return;
    }

    let newProjectDashboardLinks;

    if (isDefined(link)) {
      let newLink: ProjectDashboardLink = {
        projectId: nav.projectId,
        dashboardId: dashboardId,
        navTs: Date.now()
      };

      newProjectDashboardLinks = [
        newLink,
        ...links.filter(r => !(r.projectId === nav.projectId))
      ];
    } else {
      let newLink: ProjectDashboardLink = {
        projectId: nav.projectId,
        dashboardId: dashboardId,
        navTs: Date.now()
      };

      newProjectDashboardLinks = [newLink, ...links];
    }

    let oneYearAgoTimestamp = Date.now() - 1000 * 60 * 60 * 24 * 365;

    newProjectDashboardLinks = newProjectDashboardLinks.filter(
      l => l.navTs >= oneYearAgoTimestamp
    );

    this.uiQuery.updatePart({
      projectDashboardLinks: newProjectDashboardLinks
    });
    this.setUserUi({
      projectDashboardLinks: newProjectDashboardLinks
    });
  }

  setProjectReportLink(item: { reportId: string }) {
    let { reportId } = item;

    let nav = this.navQuery.getValue();

    if (isUndefined(reportId) || isUndefined(nav.projectId)) {
      return;
    }

    let links = this.uiQuery.getValue().projectReportLinks;

    let link: ProjectReportLink = links.find(
      l => l.projectId === nav.projectId
    );

    if (link?.reportId === reportId) {
      return;
    }

    let newProjectReportLinks;

    if (isDefined(link)) {
      let newLink: ProjectReportLink = {
        projectId: nav.projectId,
        reportId: reportId,
        navTs: Date.now()
      };

      newProjectReportLinks = [
        newLink,
        ...links.filter(r => !(r.projectId === nav.projectId))
      ];
    } else {
      let newLink: ProjectReportLink = {
        projectId: nav.projectId,
        reportId: reportId,
        navTs: Date.now()
      };

      newProjectReportLinks = [newLink, ...links];
    }

    let oneYearAgoTimestamp = Date.now() - 1000 * 60 * 60 * 24 * 365;

    newProjectReportLinks = newProjectReportLinks.filter(
      l => l.navTs >= oneYearAgoTimestamp
    );

    this.uiQuery.updatePart({ projectReportLinks: newProjectReportLinks });
    this.setUserUi({ projectReportLinks: newProjectReportLinks });
  }

  setProjectExplorerSessionLink(item: {
    sessionId: string;
    repoId: string;
    branchId: string;
    envId: string;
    tabId: string;
  }) {
    let { sessionId, repoId, branchId, envId, tabId } = item;

    let nav = this.navQuery.getValue();

    if (isUndefined(sessionId) || isUndefined(nav.projectId)) {
      return;
    }

    let links = this.uiQuery.getValue().projectExplorerSessionLinks ?? [];

    let link = links.find(l => l.projectId === nav.projectId);

    if (
      link?.sessionId === sessionId &&
      link?.repoId === repoId &&
      link?.branchId === branchId &&
      link?.envId === envId &&
      link?.tabId === tabId
    ) {
      return;
    }

    let newLink: ProjectExplorerSessionLink = {
      projectId: nav.projectId,
      sessionId: sessionId,
      repoId: repoId,
      branchId: branchId,
      envId: envId,
      tabId: tabId,
      navTs: Date.now()
    };

    let newProjectExplorerSessionLinks = [
      newLink,
      ...links.filter(r => !(r.projectId === nav.projectId))
    ];

    let oneYearAgoTimestamp = Date.now() - 1000 * 60 * 60 * 24 * 365;

    newProjectExplorerSessionLinks = newProjectExplorerSessionLinks.filter(
      l => l.navTs >= oneYearAgoTimestamp
    );

    this.uiQuery.updatePart({
      projectExplorerSessionLinks: newProjectExplorerSessionLinks
    });

    this.setUserUi({
      projectExplorerSessionLinks: newProjectExplorerSessionLinks
    });
  }

  clearProjectChartLink() {
    let nav = this.navQuery.getValue();

    if (isUndefined(nav.projectId)) {
      return;
    }

    let links = this.uiQuery.getValue().projectChartLinks;
    let newProjectChartLinks = links.filter(l => l.projectId !== nav.projectId);

    this.uiQuery.updatePart({ projectChartLinks: newProjectChartLinks });
    this.setUserUi({ projectChartLinks: newProjectChartLinks });
  }

  clearProjectModelLink() {
    let nav = this.navQuery.getValue();

    if (isUndefined(nav.projectId)) {
      return;
    }

    let links = this.uiQuery.getValue().projectModelLinks;
    let newProjectModelLinks = links.filter(l => l.projectId !== nav.projectId);

    this.uiQuery.updatePart({ projectModelLinks: newProjectModelLinks });
    this.setUserUi({ projectModelLinks: newProjectModelLinks });
  }

  clearProjectDashboardLink() {
    let nav = this.navQuery.getValue();

    if (isUndefined(nav.projectId)) {
      return;
    }

    let links = this.uiQuery.getValue().projectDashboardLinks;
    let newProjectDashboardLinks = links.filter(
      l => l.projectId !== nav.projectId
    );

    this.uiQuery.updatePart({
      projectDashboardLinks: newProjectDashboardLinks
    });
    this.setUserUi({
      projectDashboardLinks: newProjectDashboardLinks
    });
  }

  clearProjectReportLink() {
    let nav = this.navQuery.getValue();

    if (isUndefined(nav.projectId)) {
      return;
    }

    let links = this.uiQuery.getValue().projectReportLinks;
    let newProjectReportLinks = links.filter(
      l => l.projectId !== nav.projectId
    );

    this.uiQuery.updatePart({ projectReportLinks: newProjectReportLinks });
    this.setUserUi({ projectReportLinks: newProjectReportLinks });
  }
}
